import { useEffect, useRef, useState, type ReactNode } from 'react'

// MP-OS-188 round 3, bug #2 — a single row that never wraps: when its content is wider than
// the space it gets, it scrolls horizontally and fades the edge that has more content behind
// it (data-more-start / data-more-end, styled in App.css .scroll-row). Its height is one row
// whatever the number of children or the viewport width, so swapping placeholder children for
// a smaller or larger loaded set cannot move anything below it.
//
// MPD-60 (MPD-58 #1) — the native scrollbar stays hidden (it would add height only when the
// content overflows), so a mouse reaches the hidden children in two other ways:
// - a vertical wheel over the row scrolls it sideways until it reaches that end, then the
//   page scrolls as usual;
// - an arrow button overlays each edge that has more content behind it. The arrows are
//   absolutely positioned inside the row, so they never change its size. They are mouse aids
//   only: keyboard users Tab through the children (each focused child scrolls into view), touch
//   users swipe, and screen readers read every child, so the arrows are not focusable and are
//   hidden from assistive technology and on devices without hover (App.css).
//
// MP-OS-205 — the active child (the one marked `data-active`) is always in view: on mount
// (page load and returning to the page), when another child becomes active, when the loaded
// children replace placeholders, and when the row's width changes, the TRACK scrolls
// horizontally so that child is fully visible and clear of the edge arrows and fades. Only
// the track scrolls (scrollTo on it, never scrollIntoView, which may also scroll the page),
// and without animation under prefers-reduced-motion.
const EDGE_CLEARANCE = 32 // px: the 28px fade (App.css .scroll-row) and the 24px arrow sit inside it
const reducedMotion = () =>
  typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches

export function ScrollRow({ children, className = '', label }: { children: ReactNode; className?: string; label?: string }) {
  const ref = useRef<HTMLDivElement>(null)
  const [more, setMore] = useState({ start: false, end: false })
  const lastActive = useRef<Element | null>(null)
  const lastWidth = useRef<number | null>(null)
  useEffect(() => {
    const el = ref.current
    if (!el) return
    const reveal = (animate: boolean) => {
      const active = el.querySelector('[data-active]')
      if (!active) return
      const max = el.scrollWidth - el.clientWidth
      if (max <= 1) return
      const track = el.getBoundingClientRect()
      const chip = active.getBoundingClientRect()
      const start = chip.left - track.left + el.scrollLeft // the chip's offset in the content
      const end = start + chip.width
      let left = el.scrollLeft
      if (start - EDGE_CLEARANCE < left || chip.width + 2 * EDGE_CLEARANCE > el.clientWidth) left = start - EDGE_CLEARANCE
      else if (end + EDGE_CLEARANCE > left + el.clientWidth) left = end + EDGE_CLEARANCE - el.clientWidth
      left = Math.min(max, Math.max(0, Math.round(left)))
      if (Math.abs(left - el.scrollLeft) < 1) return
      if (typeof el.scrollTo === 'function') el.scrollTo({ left, behavior: animate && !reducedMotion() ? 'smooth' : 'auto' })
      else el.scrollLeft = left
    }
    // A new active child: animate only a change within the same set of children; a first
    // render or a swap from placeholders jumps there at once.
    const active = el.querySelector('[data-active]')
    if (active !== lastActive.current) {
      const animate = lastActive.current != null && el.contains(lastActive.current)
      lastActive.current = active
      lastWidth.current = el.clientWidth
      reveal(animate)
    }
    const onResize = () => {
      update()
      // Only a real width change re-reveals; the observer's first callback after observe()
      // (this effect runs on every render) must not undo the user's own scrolling.
      if (el.clientWidth === lastWidth.current) return
      lastWidth.current = el.clientWidth
      reveal(false)
    }
    const update = () => {
      const start = el.scrollLeft > 1
      const end = el.scrollLeft + el.clientWidth < el.scrollWidth - 1
      setMore((m) => (m.start === start && m.end === end ? m : { start, end }))
    }
    const onWheel = (e: WheelEvent) => {
      // Horizontal gestures (trackpad, Shift+wheel) and pinch-zoom are native already.
      if (e.ctrlKey || e.shiftKey || Math.abs(e.deltaY) <= Math.abs(e.deltaX)) return
      const max = el.scrollWidth - el.clientWidth
      if (max <= 1) return
      const px = e.deltaY * (e.deltaMode === 1 ? 16 : e.deltaMode === 2 ? el.clientWidth : 1)
      // At the end the wheel is in, let the page scroll instead of trapping it.
      if ((px < 0 && el.scrollLeft <= 0) || (px > 0 && el.scrollLeft >= max - 1)) return
      e.preventDefault()
      el.scrollLeft = Math.min(max, Math.max(0, el.scrollLeft + px))
    }
    update()
    el.addEventListener('scroll', update, { passive: true })
    el.addEventListener('wheel', onWheel, { passive: false })
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(onResize) : null
    ro?.observe(el)
    if (el.firstElementChild) ro?.observe(el.firstElementChild)
    return () => { el.removeEventListener('scroll', update); el.removeEventListener('wheel', onWheel); ro?.disconnect() }
  })
  const step = (dir: -1 | 1) => {
    const el = ref.current
    if (!el) return
    const left = dir * Math.max(40, Math.round(el.clientWidth * 0.8))
    if (typeof el.scrollBy === 'function') el.scrollBy({ left, behavior: 'smooth' })
    else el.scrollLeft += left
  }
  const arrow = (dir: -1 | 1) => (
    <button type="button" tabIndex={-1} aria-hidden="true" title={dir < 0 ? 'Scroll left' : 'Scroll right'}
      className={`scroll-row-arrow scroll-row-arrow-${dir < 0 ? 'start' : 'end'}`}
      // Keep focus where it is (a click would otherwise move it onto this unfocusable aid).
      onMouseDown={(e) => e.preventDefault()} onClick={() => step(dir)}>
      {dir < 0 ? '‹' : '›'}
    </button>
  )
  return (
    <div className={`scroll-row ${className}`.trim()} data-more-start={more.start || undefined} data-more-end={more.end || undefined}>
      <div ref={ref} className="scroll-row-track" role={label ? 'group' : undefined} aria-label={label}>
        {children}
      </div>
      {more.start && arrow(-1)}
      {more.end && arrow(1)}
    </div>
  )
}
